import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GeneratingState } from "@/components/GeneratingState";
import { QuizRunner } from "@/components/QuizRunner";
import { ScoreRing } from "@/components/ScoreRing";

const NAMES = { basic: "Basic", intermediate: "Intermediate", advanced: "Advanced" };

export default function Placement() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      setQuiz((await api.post("/placement/generate", { language: lang })).data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const submit = async (answers) => {
    setSubmitting(true);
    try {
      setResult((await api.post("/placement/submit", { quiz_id: quiz.quiz_id, answers })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const start = async (level) => {
    await api.post("/progress/start", { language: lang, level });
    await refreshUser();
    navigate(`/learn/${lang}`);
  };

  if (!quiz) return <GeneratingState title="Building your placement test" error={error} onRetry={load} />;
  if (!result)
    return (
      <div className="px-4 py-10">
        <p className="text-center caption mb-6">{quiz.language.name} placement test</p>
        <QuizRunner questions={quiz.questions} lang={lang} onSubmit={submit} submitting={submitting} onExit={() => navigate("/languages")} />
      </div>
    );

  return (
    <div className="max-w-xl mx-auto px-4 py-12 text-center animate-fade-up" data-testid="placement-result">
      <div className="flex justify-center"><ScoreRing score={result.score} passed /></div>
      <p className="caption mt-6">We suggest</p>
      <h1 data-testid="placement-suggested-level" className="font-display text-4xl font-extrabold text-slate-900">{NAMES[result.suggested_level]}</h1>
      <div className="grid grid-cols-3 gap-3 mt-8">
        {Object.entries(result.by_level).map(([lv, s]) => (
          <div key={lv} className="rounded-2xl bg-white border border-slate-200 p-4">
            <p className="text-xs font-bold uppercase text-slate-500">{NAMES[lv]}</p>
            <p data-testid={`placement-score-${lv}`} className="font-display text-2xl font-extrabold text-slate-900">{s}%</p>
          </div>
        ))}
      </div>
      <button data-testid="placement-start-suggested-button" onClick={() => start(result.suggested_level)} className="btn-primary w-full mt-8">
        Start at {NAMES[result.suggested_level]}
      </button>
      <div className="flex gap-2 justify-center mt-4 flex-wrap">
        {Object.keys(NAMES).filter((l) => l !== result.suggested_level).map((lv) => (
          <button key={lv} data-testid={`placement-start-${lv}-button`} onClick={() => start(lv)} className="btn-ghost !py-2 text-sm">Choose {NAMES[lv]} instead</button>
        ))}
      </div>
    </div>
  );
}
